import React, { useState, useEffect, ReactNode } from "react";
import Modal from "react-modal";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import CreateEntityDrawer from "@/components/CreateEntityDrawer/CreateEntityDrawer";
import { CheckIcon } from "@heroicons/react/24/outline";
import { cn } from "@/utils/utils";

export type BaseEntity = {
  id: string | number;
  name: string;
  color?: Color | null;
};

export interface Color {
  id: number;
  value: string;
}

export type FetchEntitiesFn<T extends BaseEntity> = (params: {
  searchTerm: string;
  page: number;
  pageSize: number;
}) => Promise<{
  data: T[];
  totalCount: number;
}>;

export type CreateEntityFn<T extends BaseEntity> = (params: {
  name: string;
}) => Promise<T>;

export interface EntityPickerModalProps<T extends BaseEntity> {
  isOpen: boolean;
  onClose: () => void;
  fetchEntities: FetchEntitiesFn<T>;
  createEntity?: CreateEntityFn<T>;
  initialSelectedEntities: T[];
  onConfirm: (selectedEntities: T[]) => void;
  entityNameSingular?: string;
  entityNamePlural?: string;
  renderEntityRow?: (
    entity: T,
    isSelected: boolean,
    toggle: (ent: T) => void,
  ) => ReactNode;
}

export default function EntityPickerModal<T extends BaseEntity>({
  isOpen,
  onClose,
  fetchEntities,
  createEntity,
  initialSelectedEntities,
  onConfirm,
  entityNameSingular = "Entity",
  entityNamePlural = "Entities",
  renderEntityRow,
}: EntityPickerModalProps<T>) {
  const [searchTerm, setSearchTerm] = useState("");
  const [entities, setEntities] = useState<T[]>([]);
  const [selectedEntities, setSelectedEntities] = useState<T[]>(
    initialSelectedEntities,
  );
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const headingText = `Please Select ${entityNamePlural}`;

  useEffect(() => {
    if (isOpen) {
      setSelectedEntities(initialSelectedEntities);
      setSearchTerm("");
      setEntities([]);
      setPage(1);
      setHasMore(true);
      setIsLoadingInitial(false);
      setShowCreateDrawer(false);
    }
  }, [isOpen, initialSelectedEntities]);

  useEffect(() => {
    if (!isOpen) return;
    setEntities([]);
    setPage(1);
    setHasMore(true);
    loadEntities(1, debouncedSearchTerm);
  }, [isOpen, debouncedSearchTerm]);

  const loadEntities = (pageNumber: number, sTerm: string) => {
    if (pageNumber === 1) setIsLoadingInitial(true);
    fetchEntities({
      searchTerm: sTerm,
      page: pageNumber,
      pageSize: 10,
    })
      .then((res) => {
        const newData = res.data;
        setEntities((prev) =>
          pageNumber === 1 ? newData : [...prev, ...newData],
        );
        if (newData.length < 10) {
          setHasMore(false);
        }
      })
      .catch(() => {
        setHasMore(false);
      })
      .finally(() => setIsLoadingInitial(false));
  };

  const fetchNext = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadEntities(nextPage, debouncedSearchTerm);
  };

  const toggleSelection = (ent: T) => {
    setSelectedEntities((prev) => {
      const alreadySelected = prev.some((x) => x.id === ent.id);
      return alreadySelected
        ? prev.filter((x) => x.id !== ent.id)
        : [...prev, ent];
    });
  };

  const handleEntityCreated = (newEntity: T) => {
    setEntities((prev) => [newEntity, ...prev]);
    setSelectedEntities((prev) => [...prev, newEntity]);
  };

  const defaultRenderRow = (ent: T, isSelected: boolean) => (
    <button
      className={cn(
        "w-full px-3 py-2 text-left transition-colors",
        isSelected
          ? "bg-gray-100 dark:bg-slate-800"
          : "hover:bg-gray-50 dark:hover:bg-slate-800/50",
      )}
      key={ent.id}
      onClick={() => toggleSelection(ent)}
      type="button"
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium text-gray-900 dark:text-white truncate">
            {ent.name}
          </div>
        </div>
        {isSelected && (
          <CheckIcon className="h-3.5 w-3.5 text-primary flex-shrink-0 ml-2" />
        )}
      </div>
    </button>
  );

  const renderRow = (ent: T) => {
    const isSelected = selectedEntities.some((x) => x.id === ent.id);
    if (renderEntityRow) {
      return (
        <React.Fragment key={ent.id}>
          {renderEntityRow(ent, isSelected, toggleSelection)}
        </React.Fragment>
      );
    }
    return defaultRenderRow(ent, isSelected);
  };

  return (
    <Modal
      className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-sm flex flex-col p-0 bg-white dark:bg-slate-900 shadow-lg border border-gray-200 dark:border-slate-700"
      isOpen={isOpen}
      onRequestClose={onClose}
      overlayClassName="fixed inset-0 bg-black/20 z-[9999]"
    >
      {/* Header */}
      <div className="flex-shrink-0 px-3 py-2.5 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
        <h2 className="text-sm font-medium text-gray-900 dark:text-white">
          {headingText}
        </h2>
        <div className="flex items-center gap-2">
          {createEntity && (
            <button
              className="text-[12px] font-medium text-primary hover:text-primary-dark transition-colors"
              onClick={() => setShowCreateDrawer(true)}
              type="button"
            >
              + New
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="flex-shrink-0 px-3 py-2.5 border-b border-gray-200 dark:border-slate-700">
        <input
          className="w-full border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 pl-3 pr-3 py-1.5 text-[13px] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-gray-300 dark:focus:border-slate-600 focus:outline-none transition-colors"
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={`Search ${entityNamePlural.toLowerCase()}...`}
          type="text"
          value={searchTerm}
        />
      </div>

      {/* Entity List */}
      <div
        className="flex-1 overflow-auto max-h-[280px]"
        id="infinite-scroller-parent"
      >
        {isLoadingInitial ? (
          <div className="flex items-center justify-center h-[280px]">
            <LoadingIndicator isFullScreen={false} />
          </div>
        ) : (
          <InfiniteScroll
            dataLength={entities.length}
            endMessage={
              entities.length > 0 ? (
                <div className="py-2 text-center text-[12px] text-gray-400">
                  End of list
                </div>
              ) : (
                <div className="py-6 text-center">
                  <p className="text-[13px] text-gray-500 dark:text-gray-400">
                    {`No ${entityNamePlural.toLowerCase()} found`}
                  </p>
                </div>
              )
            }
            hasMore={hasMore}
            loader={
              <p className="text-center py-2 text-[12px] text-gray-400">
                Loading more...
              </p>
            }
            next={fetchNext}
            scrollableTarget="infinite-scroller-parent"
            style={{ overflow: "visible" }}
          >
            <div>{entities.map(renderRow)}</div>
          </InfiniteScroll>
        )}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 flex gap-2 px-3 py-2.5 border-t border-gray-200 dark:border-slate-700">
        <button
          className="flex-1 px-2.5 py-1.5 text-[13px] font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          onClick={onClose}
          type="button"
        >
          Cancel
        </button>
        <button
          className="flex-1 px-2.5 py-1.5 text-[13px] font-medium text-white bg-primary hover:bg-primary/90 transition-colors"
          onClick={() => onConfirm(selectedEntities)}
          type="button"
        >
          Confirm
        </button>
      </div>

      {createEntity && (
        <CreateEntityDrawer
          createEntity={createEntity}
          entityNameSingular={entityNameSingular}
          isOpen={showCreateDrawer}
          onClose={() => setShowCreateDrawer(false)}
          onEntityCreated={handleEntityCreated}
        />
      )}
    </Modal>
  );
}
